import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  adminClientsService,
  type ClientDetail,
  type ClientDomain,
  type ClientIngestionPatch,
} from '../../../services/adminClientsService';
import { toastSuccess, toastError } from '../../../lib/toast';
import type { AutonomousTabHandle } from './IdentityTab';
import { SystemTenantNotice } from './SystemTenantNotice';

interface Props {
  clientId: string;
  initialData: ClientDetail;
  onSaved?: (updated: ClientDetail) => void;
}

interface LocalState {
  ingestion_modes_enabled: Record<string, boolean>;
  s3_polling_policy: 'interval' | 'scheduled';
  s3_polling_interval_minutes: number | null;
  s3_polling_cron: string | null;
  s3_polling_prefix: string | null;
}

const toLocal = (c: ClientDetail): LocalState => ({
  ingestion_modes_enabled: { ...(c.ingestion_modes_enabled || {}) },
  s3_polling_policy: c.s3_polling_policy || 'interval',
  s3_polling_interval_minutes: c.s3_polling_interval_minutes ?? 1,
  s3_polling_cron: c.s3_polling_cron,
  s3_polling_prefix: c.s3_polling_prefix,
});

export const IngestionTab = forwardRef<AutonomousTabHandle, Props>(
  ({ clientId, initialData, onSaved }, ref) => {
    const [state, setState] = useState<LocalState>(() => toLocal(initialData));
    const [baseline, setBaseline] = useState<LocalState>(() => toLocal(initialData));
    const [saving, setSaving] = useState(false);
    const [domains, setDomains] = useState<ClientDomain[]>([]);

    const isSystem = initialData.is_system_tenant === true;
    const s3Enabled = !!state.ingestion_modes_enabled.s3_polling;
    // Force dirty si polling activé mais champs requis NULL en BD (cas où toLocal()
    // a appliqué un fallback masquant la divergence — sans ça l'utilisateur ne peut
    // pas sauvegarder car state == baseline).
    const needsBackfill =
      s3Enabled &&
      ((initialData.s3_polling_policy == null) ||
        (initialData.s3_polling_policy === 'interval' && initialData.s3_polling_interval_minutes == null) ||
        (initialData.s3_polling_policy === 'scheduled' && initialData.s3_polling_cron == null));
    const isDirty =
      JSON.stringify(state) !== JSON.stringify(baseline) || needsBackfill;

    useImperativeHandle(ref, () => ({
      getIsDirty: () => isDirty,
      reset: () => setState(baseline),
    }));

    useEffect(() => {
      if (!s3Enabled) return;
      let cancelled = false;
      adminClientsService
        .listDomains(clientId)
        .then((rows) => {
          if (!cancelled) setDomains(rows);
        })
        .catch((e) => console.error('listDomains failed:', e));
      return () => {
        cancelled = true;
      };
    }, [clientId, s3Enabled]);

    const setMode = (key: string, value: boolean) => {
      setState((s) => ({
        ...s,
        ingestion_modes_enabled: { ...s.ingestion_modes_enabled, [key]: value },
      }));
    };

    const handleSave = async () => {
      if (!isDirty) return;
      setSaving(true);
      try {
        const payload: ClientIngestionPatch = {};
        if (
          JSON.stringify(state.ingestion_modes_enabled) !==
          JSON.stringify(baseline.ingestion_modes_enabled)
        ) {
          payload.ingestion_modes_enabled = state.ingestion_modes_enabled;
        }
        // Quand s3_polling est activé, on envoie toujours la policy et son champ
        // associé (interval_minutes ou cron). Sinon, le fallback `?? 1` / `|| 'interval'`
        // de toLocal() rend state == baseline alors qu'en BD ces champs sont NULL,
        // et le worker poll_due_tenants skip indéfiniment (policy=interval mais
        // s3_polling_interval_minutes=None).
        if (s3Enabled) {
          payload.s3_polling_policy = state.s3_polling_policy;
          if (state.s3_polling_policy === 'interval') {
            payload.s3_polling_interval_minutes = state.s3_polling_interval_minutes ?? 1;
          } else if (state.s3_polling_policy === 'scheduled') {
            payload.s3_polling_cron = state.s3_polling_cron;
          }
        } else {
          if (state.s3_polling_policy !== baseline.s3_polling_policy)
            payload.s3_polling_policy = state.s3_polling_policy;
          if (state.s3_polling_interval_minutes !== baseline.s3_polling_interval_minutes)
            payload.s3_polling_interval_minutes = state.s3_polling_interval_minutes;
          if (state.s3_polling_cron !== baseline.s3_polling_cron)
            payload.s3_polling_cron = state.s3_polling_cron;
        }
        if (state.s3_polling_prefix !== baseline.s3_polling_prefix)
          payload.s3_polling_prefix = state.s3_polling_prefix;

        const updated = await adminClientsService.patchIngestion(clientId, payload);
        setBaseline(toLocal(updated));
        setState(toLocal(updated));
        toastSuccess('Ingestion enregistrée');
        onSaved?.(updated);
      } catch (err) {
        toastError('Enregistrement échoué', err);
      } finally {
        setSaving(false);
      }
    };

    const activeDomains = domains.filter((d) => d.is_active);
    const modes = state.ingestion_modes_enabled;

    return (
      <div className="space-y-4 max-w-2xl">
        {isSystem && <SystemTenantNotice what="modes d'ingestion non applicables" />}
        <h3 className="text-sm font-semibold text-gray-700">Modes d'ingestion</h3>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!modes.ui_upload}
            disabled={isSystem}
            onChange={(e) => setMode('ui_upload', e.target.checked)}
          />
          <span className="text-sm">Upload via interface (UI)</span>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={s3Enabled}
            disabled={isSystem}
            onChange={(e) => setMode('s3_polling', e.target.checked)}
          />
          <span className="text-sm">Polling S3</span>
        </label>

        <label className="flex items-center gap-2 opacity-50 cursor-not-allowed" title="Disponible en Phase 2">
          <input
            type="checkbox"
            checked={!!modes.push_register}
            disabled
          />
          <span className="text-sm">Push register (Phase 2)</span>
        </label>

        {s3Enabled && (
          <div className="border border-gray-200 rounded p-4 space-y-3 bg-gray-50">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Mode de polling
              </label>
              <select
                value={state.s3_polling_policy}
                disabled={isSystem}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    s3_polling_policy: e.target.value as 'interval' | 'scheduled',
                  }))
                }
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm disabled:bg-gray-100"
              >
                <option value="interval">Intervalle régulier</option>
                <option value="scheduled">Cron (planifié)</option>
              </select>
            </div>

            <div className="pt-1">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!modes.s3_redrop_reextract}
                  disabled={isSystem}
                  onChange={(e) => setMode('s3_redrop_reextract', e.target.checked)}
                />
                <span className="text-sm">
                  Ré-extraction au re-dépôt S3
                  <span className="block text-[11px] text-gray-500">
                    Re-déposer dans <code>incoming/</code> un PDF déjà traité crée une
                    nouvelle version à valider (au lieu d'être ignoré).
                  </span>
                </span>
              </label>
            </div>

            {state.s3_polling_policy === 'interval' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Intervalle de polling (minutes)
                </label>
                <input
                  type="number"
                  min={1}
                  value={state.s3_polling_interval_minutes ?? 1}
                  disabled={isSystem}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      s3_polling_interval_minutes: parseInt(e.target.value, 10) || 1,
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm disabled:bg-gray-100"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Le tenant est pollé dès que ce délai est écoulé depuis le dernier poll.
                  Granularité réelle : 1 minute (tick global du scheduler).
                </p>
              </div>
            )}

            {state.s3_polling_policy === 'scheduled' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Expression cron
                </label>
                <input
                  type="text"
                  value={state.s3_polling_cron ?? ''}
                  disabled={isSystem}
                  onChange={(e) =>
                    setState((s) => ({ ...s, s3_polling_cron: e.target.value || null }))
                  }
                  placeholder="0 2 * * *"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono disabled:bg-gray-100"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Format : <code>min h jour mois jourSemaine</code>. Exemples :
                  <code className="ml-1">0 2 * * *</code> (chaque nuit 02h),
                  <code className="ml-1">*/15 * * * *</code> (toutes les 15 min),
                  <code className="ml-1">0 9,17 * * 1-5</code> (9h et 17h en semaine).
                </p>
              </div>
            )}

            {initialData.last_polled_at && (
              <p className="text-[11px] text-gray-500">
                Dernier poll : {new Date(initialData.last_polled_at).toLocaleString()}
              </p>
            )}

            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-700 mb-2">
                Zones S3 sous le préfixe tenant (toutes relatives au <code className="text-[11px]">key_prefix</code> du backend)
              </p>
              <ul className="space-y-1 text-xs font-mono text-gray-700">
                {activeDomains.length === 0 ? (
                  <li className="text-amber-700">
                    ⚠ Aucun domaine actif — configurez l'onglet « Domaines &amp; Configs » avant
                    d'utiliser le polling S3.
                  </li>
                ) : (
                  activeDomains.flatMap((d) => [
                    <li key={`${d.domain_code}-in`}>
                      <span className="text-green-700">↓ dépôt</span>{' '}
                      <code>incoming/{d.domain_code}/</code>
                    </li>,
                    <li key={`${d.domain_code}-inv`} className="pl-4">
                      <span className="text-gray-500">⚠ quarantaine domaine</span>{' '}
                      <code>incoming/{d.domain_code}/_invalid/</code>
                      <span className="text-gray-500"> (profondeur invalide)</span>
                    </li>,
                  ])
                )}
                <li>
                  <span className="text-gray-500">⚠ quarantaine globale</span>{' '}
                  <code>incoming/_invalid/</code>
                  <span className="text-gray-500"> (domaine inconnu / racine)</span>
                </li>
                <li>
                  <span className="text-gray-500">→ archivé</span>{' '}
                  <code>documents/{'{doc_id}'}/</code>
                  <span className="text-gray-500"> (PDF après ingestion, à perpétuité)</span>
                </li>
                <li>
                  <span className="text-gray-500">→ doublons</span>{' '}
                  <code>duplicates/{'{etag}'}/</code>
                  <span className="text-gray-500"> (même etag déjà ingéré)</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving || isSystem}
            className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    );
  },
);

IngestionTab.displayName = 'IngestionTab';

export default IngestionTab;
